import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CommonService } from '../../shared/core.index';
import { ApiHandlerService } from '../../shared/service/api-handler/api-handler.service';

@Injectable({
  providedIn: 'root'
})
export class ConferenceRoomService {
  private sidebarData$ = new BehaviorSubject<any[]>([]);

  // Cache for org units to avoid repeated API calls
  private orgUnitsCache: any = null;
  private orgUnitsCacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(
    private commonService: CommonService,
    private apiHandlerService: ApiHandlerService
  ) {
    this.buildSidebarData();
  }

  getBookingById(id: string): Observable<any> {
    return this.apiHandlerService.doGet<any>(`api/conference-room/bookings/${id}`, true, false);
  }

  getMyBookingCounts(): Observable<any> {
    return this.apiHandlerService.doGet<any>('api/conference-room/bookings/my-counts', true, false);
  }

  getMyBookingsList(params?: any): Observable<any> {
    return this.apiHandlerService.doGet<any>('api/conference-room/bookings/my-list', true, false, params);
  }

  getBookingUsers(search?: string): Observable<any> {
    const params: any = { exclude_self: 'true' };
    if (search) params.search = search;
    return this.apiHandlerService.doGet<any>('api/conference-room/bookings/users', true, false, params);
  }

  private buildSidebarData() {
    const sidebar = [
      {
        title: 'Dashboard',
        hasSubRoute: false,
        hideInFrame: false,
        icon: '<i class="fa-solid fa-chart-pie"></i>',
        route: '/conference-room/dashboard',
        allowed_roles: []
      },
      {
        title: 'My Booking',
        hasSubRoute: false,
        hideInFrame: false,
        icon: '<i class="fa-regular fa-calendar-days"></i>',
        route: '/conference-room/my-bookings',
        allowed_roles: []
      },
      {
        title: 'Room Display',
        hasSubRoute: false,
        hideInFrame: false,
        icon: '<i class="fa-solid fa-tv"></i>',
        route: '/conference-room/room-display',
        allowed_roles: ['cr-room-display']
      },
      {
        title: 'Approval Requests',
        hasSubRoute: false,
        hideInFrame: false,
        icon: '<i class="fa-solid fa-clipboard-check"></i>',
        route: '/conference-room/approval-requests',
        allowed_roles: ['cr-approvals']
      },
      {
        title: 'Reports',
        hasSubRoute: true,
        hideInFrame: false,
        icon: '<i class="fa-solid fa-chart-bar"></i>',
        route: '/conference-room/reports',
        showSubRoute: false,
        allowed_roles: ['cr-reports'],
        subMenu: [
          {
            title: 'Room Utilisation',
            route: '/conference-room/reports/room-utilisation',
            icon: '<i class="fa-solid fa-wave-square"></i>',
            allowed_roles: ['cr-reports'],
            hasSubRoute: false,
          },
          {
            title: 'Booking Summary',
            route: '/conference-room/reports/booking-summary',
            icon: '<i class="fa-regular fa-file-lines"></i>',
            allowed_roles: ['cr-reports'],
            hasSubRoute: false,
          },
          {
            title: 'Approval Tracking',
            route: '/conference-room/reports/approval-tracking',
            icon: '<i class="fa-regular fa-clipboard-check"></i>',
            allowed_roles: ['cr-reports'],
            hasSubRoute: false,
          }
        ]
      },
      {
        title: 'Settings',
        hasSubRoute: true,
        hideInFrame: false,
        icon: '<i class="fa-solid fa-gear"></i>',
        route: '/conference-room/settings',
        showSubRoute: false,
        allowed_roles: ['cr-amenities', 'cr-conference-room-master'],
        subMenu: [
          {
            title: 'Amenities',
            route: '/conference-room/settings/amenities',
            icon: '<i class="fa-solid fa-lightbulb"></i>',
            allowed_roles: ['cr-amenities'],
            hasSubRoute: false,
          },
          {
            title: 'Conference Rooms',
            route: '/conference-room/settings/conference-rooms',
            icon: '<i class="fa-solid fa-door-open"></i>',
            allowed_roles: ['cr-conference-room-master'],
            hasSubRoute: false,
          }
        ]
      }
    ];

    // Push into BehaviorSubject
    this.sidebarData$.next(sidebar);

    // Update CommonService
    this.commonService.setMenuItem(sidebar);
  }

  // Observable for components to subscribe to
  getSidebarData(): Observable<any[]> {
    // Rebuild sidebar data each time this is requested to ensure
    // CommonService.menuItems$ is updated when navigating between modules.
    this.buildSidebarData();
    return this.sidebarData$.asObservable();
  }

  // Placeholder methods for future API integration
  getBookings(params?: any): Observable<any> {
    let queryParams: any = {};
    if (params) queryParams = { ...params };
    return this.apiHandlerService.doGet<any>('api/conference-room/bookings', true, false, queryParams);
  }

  checkAvailability(params: any): Observable<any> {
    return this.apiHandlerService.doGet<any>('api/conference-room/bookings/check-availability', false, true, params);
  }

  createBooking(data: any): Observable<any> {
    return this.apiHandlerService.doPost<any>('api/conference-room/bookings', data, false, true);
  }

  updateBooking(id: string, data: any): Observable<any> {
    return this.apiHandlerService.doPut<any>(`api/conference-room/bookings/${id}`, data, false, true);
  }

  deleteBooking(id: string): Observable<any> {
    // TODO: Implement API call when backend is ready
    return new Observable();
  }

  cancelBooking(id: string): Observable<any> {
    return this.apiHandlerService.doDelete<any>(`api/conference-room/bookings/${id}/cancel`, false, true);
  }

  // Amenities API methods
  getAmenities(page?: number, pageSize?: number, params?: any): Observable<any> {
    let queryParams: any = {};
    if (page !== undefined) {
      queryParams.page = page.toString();
    }
    if (pageSize !== undefined) {
      queryParams.pageSize = pageSize.toString();
    }
    // Merge additional params (search, is_active, etc.)
    if (params) {
      queryParams = { ...queryParams, ...params };
    }
    return this.apiHandlerService.doGet<any>('api/conference-room/amenities', true, true, queryParams);
  }

  getAmenityById(amenityId: string): Observable<any> {
    return this.apiHandlerService.doGet<any>(`api/conference-room/amenities/${amenityId}`, true, true);
  }

  addAmenity(data: any): Observable<any> {
    return this.apiHandlerService.doPost<any>('api/conference-room/amenities', data, true, true);
  }

  updateAmenity(id: string, data: any): Observable<any> {
    return this.apiHandlerService.doPut<any>(`api/conference-room/amenities/${id}`, data, true, true);
  }

  deleteAmenity(id: string): Observable<any> {
    return this.apiHandlerService.doDelete<any>(`api/conference-room/amenities/${id}`, true, true);
  }

  // Conference Rooms API methods
  getConferenceRooms(page?: number, pageSize?: number, params?: any): Observable<any> {
    let queryParams: any = {};
    if (page !== undefined) {
      queryParams.page = page.toString();
    }
    if (pageSize !== undefined) {
      queryParams.pageSize = pageSize.toString();
    }
    // Merge additional params (search, filters, etc.)
    if (params) {
      queryParams = { ...queryParams, ...params };
    }
    return this.apiHandlerService.doGet<any>('api/conference-room/conference', true, true, queryParams);
  }

  getConferenceRoomById(roomId: string): Observable<any> {
    return this.apiHandlerService.doGet<any>(`api/conference-room/conference/${roomId}`, true, true);
  }

  addConferenceRoom(data: any): Observable<any> {
    return this.apiHandlerService.doPost<any>('api/conference-room/conference', data, true, true);
  }

  updateConferenceRoom(id: string, data: any): Observable<any> {
    return this.apiHandlerService.doPut<any>(`api/conference-room/conference/${id}`, data, true, true);
  }

  deleteConferenceRoom(id: string): Observable<any> {
    return this.apiHandlerService.doDelete<any>(`api/conference-room/conference/${id}`, true, true);
  }

  // Maintenance API methods
  getMaintenances(confId: string, params?: any): Observable<any> {
    return this.apiHandlerService.doGet<any>(`api/conference-room/conference/${confId}/maintenance`, true, true, params);
  }

  getMaintenanceById(confId: string, id: string): Observable<any> {
    return this.apiHandlerService.doGet<any>(`api/conference-room/conference/${confId}/maintenance/${id}`, true, true);
  }

  createMaintenance(confId: string, data: any): Observable<any> {
    return this.apiHandlerService.doPost<any>(`api/conference-room/conference/${confId}/maintenance`, data, true, true);
  }

  updateMaintenance(confId: string, id: string, data: any): Observable<any> {
    return this.apiHandlerService.doPut<any>(`api/conference-room/conference/${confId}/maintenance/${id}`, data, true, true);
  }

  deleteMaintenance(confId: string, id: string): Observable<any> {
    return this.apiHandlerService.doDelete<any>(`api/conference-room/conference/${confId}/maintenance/${id}`, true, true);
  }

  // Get organizational units for plant dropdown
  getOrgUnits(): Observable<any> {
    // Check if cache is valid (less than 5 minutes old)
    const now = Date.now();
    if (this.orgUnitsCache && (now - this.orgUnitsCacheTime) < this.CACHE_DURATION) {
      return new Observable(observer => {
        observer.next(this.orgUnitsCache);
        observer.complete();
      });
    }

    // Cache is invalid or doesn't exist, fetch from API
    const params = { is_all: 'true' };
    return this.apiHandlerService.doGet<any>('api/admin/orgunits', true, true, params).pipe(
      tap(response => {
        // Cache the response
        this.orgUnitsCache = response;
        this.orgUnitsCacheTime = now;
      })
    );
  }

  // Method to clear org units cache when needed
  clearOrgUnitsCache(): void {
    this.orgUnitsCache = null;
    this.orgUnitsCacheTime = 0;
  }

  // Get users with cr-approvals role
  getApprovers(params?: any): Observable<any> {
    return this.apiHandlerService.doGet<any>('api/conference-room/conference/approvers', true, true, params);
  }

  // Approval Requests
  getApprovalRequests(params: { tab: string; page?: number; limit?: number; conference_id?: string[]; plant_id?: number[]; requested_by?: string }): Observable<any> {
    return this.apiHandlerService.doGet<any>('api/conference-room/approval-requests', true, false, params);
  }

  // Reports
  getRoomUtilisationReport(params?: { from_date?: string; to_date?: string; plant_ids?: number[]; conference_room_ids?: string[]; page?: number; limit?: number; export?: boolean }): Observable<any> {
    const queryParams: any = {};
    if (params?.from_date) queryParams.from_date = params.from_date;
    if (params?.to_date) queryParams.to_date = params.to_date;
    if (params?.plant_ids && params.plant_ids.length > 0) queryParams.plant_ids = params.plant_ids;
    if (params?.conference_room_ids && params.conference_room_ids.length > 0) queryParams.conference_room_ids = params.conference_room_ids;
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.export !== undefined) queryParams.export = params.export;
    return this.apiHandlerService.doGet<any>('api/conference-room/reports/room-utilisation-report', true, false, queryParams);
  }

  getBookingSummaryReport(params?: { from_date?: string; to_date?: string; plant_ids?: number[]; conference_room_ids?: string[]; page?: number; limit?: number; export?: boolean }): Observable<any> {
    const queryParams: any = {};
    if (params?.from_date) queryParams.from_date = params.from_date;
    if (params?.to_date) queryParams.to_date = params.to_date;
    if (params?.plant_ids && params.plant_ids.length > 0) queryParams.plant_ids = params.plant_ids;
    if (params?.conference_room_ids && params.conference_room_ids.length > 0) queryParams.conference_room_ids = params.conference_room_ids;
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.export !== undefined) queryParams.export = params.export;
    return this.apiHandlerService.doGet<any>('api/conference-room/reports/booking-summary-report', true, false, queryParams);
  }

  getApprovalTrackingReport(params?: { from_date?: string; to_date?: string; plant_ids?: number[]; conference_room_ids?: string[]; page?: number; limit?: number; export?: boolean }): Observable<any> {
    const queryParams: any = {};
    if (params?.from_date) queryParams.from_date = params.from_date;
    if (params?.to_date) queryParams.to_date = params.to_date;
    if (params?.plant_ids && params.plant_ids.length > 0) queryParams.plant_ids = params.plant_ids;
    if (params?.conference_room_ids && params.conference_room_ids.length > 0) queryParams.conference_room_ids = params.conference_room_ids;
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.export !== undefined) queryParams.export = params.export;
    return this.apiHandlerService.doGet<any>('api/conference-room/reports/approval-tracking-report', true, false, queryParams);
  }

  // Dashboard
  getDashboardData(params?: { plant_ids?: number[]; from_date?: string; to_date?: string }): Observable<any> {
    const queryParams: any = {};
    if (params?.from_date) queryParams.from_date = params.from_date;
    if (params?.to_date) queryParams.to_date = params.to_date;
    if (params?.plant_ids && params.plant_ids.length > 0) {
      queryParams.plant_ids = params.plant_ids;
    }
    return this.apiHandlerService.doGet<any>('api/conference-room/dashboard', true, false, queryParams);
  }
}
